import React, {useEffect, useRef} from 'react';
import {useParams} from "react-router-dom";
import {useDispatch, useSelector} from 'react-redux'

import {postPHP, queryPHP} from '../utils/utils'
import {mergeBilder} from '../utils/merge'
import {getPersistentStorage, getSession, setSelMode, setTab, setTeam, setUnit, setUploading, storePlan, storePlanOriginal} from '../utils/redux'
import {DialogManager, openDialog, openDialogView} from './components/dialog';
import UploadDlg from './components/uploadDlg'
import MergeReportDlg from './components/mergeReportDlg'
import TabTable from './tabs/tabTable'
import TabBild from './tabs/tabBild'
import TabInfo from './tabs/tabInfo'

export default function Bilderplan(props) {
    let params = useParams();
    const param = (params.team ?? '').toLowerCase()
    const dispatch = useDispatch()

    const glob = {...useSelector(getPersistentStorage), ...useSelector(getSession)};

    const windowSize = useRef([window.innerWidth, window.innerHeight]);
    useEffect(() => {
        dispatch(setTeam(param));
        let unit = Math.min(windowSize.current[0] - 80, windowSize.current[1] - 300) / 16
        dispatch(setUnit(unit))
    }, [param, dispatch]);


    useEffect(() => {
        queryPHP("formationBilder", {team: glob.team}, data => {
            if (glob.plan.id === data.id) return
            let loaded = new Date().toLocaleTimeString()
            let plan = {...data, loaded}
            let maxId = plan.bilder.reduce((c, i) => Math.max(c, i.id ?? 0), 0)
            plan.maxId = maxId
            dispatch(storePlanOriginal({team: glob.team, plan}))
            dispatch(storePlan({team: glob.team, plan}))
        });
    }, [glob.team, glob.plan.id, dispatch])

    if (glob.team !== param) return <div></div>; // beim ersten Aufruf ist das Team noch nicht gesetzt

    const upload = () => {
        let copy = structuredClone(glob.plan)
        for (let i = 0; i < copy.bilder.length - 1; i++) {
            let bild = copy.bilder[i]
            if (bild.comment) bild.comment = bild.comment.trim()
        }
        let json = JSON.stringify(copy.bilder).replaceAll("\\n", "\\\\n")
        dispatch(setUploading(true))
        postPHP("uploadFormationBilder", {json, pairs: copy.pairs, team: glob.team, baseId: copy.id ?? ''}, res => {
            copy.id = res
            copy.changed = false
            copy.saved = new Date().toString()
            dispatch(storePlanOriginal({team: glob.team, plan: copy}))
            dispatch(storePlan({team: glob.team, plan: copy}))
            dispatch(setUploading(false))
        }, err => {
            dispatch(setUploading(false))
            if (err === 'CONFLICT') {
                mergeWithLatest()
            } else {
                alert("Fehler beim Upload: " + err)
            }
        });
    }

    // Jemand anderes hat zwischenzeitlich gespeichert (Server hat den Upload deshalb abgelehnt).
    // Statt den eigenen Stand einfach zu verwerfen, wird er per Drei-Wege-Merge (Basis/eigen/neu)
    // automatisch mit dem aktuellen Serverstand zusammengefuehrt. Nicht ueberlappende Aenderungen
    // werden dabei automatisch uebernommen, echte Konflikte muessen manuell geprueft werden
    const mergeWithLatest = () => {
        queryPHP("formationBilder", {team: glob.team}, data => {
            const {merged, autoNotes, conflictNotes} = mergeBilder(glob.planOriginal.bilder, glob.plan.bilder, data.bilder)
            let loaded = new Date().toLocaleTimeString()
            let maxId = merged.reduce((c, i) => Math.max(c, i.id ?? 0), 0)
            let newOriginal = {...data, loaded, maxId}
            dispatch(storePlanOriginal({team: glob.team, plan: newOriginal}))
            dispatch(storePlan({team: glob.team, plan: {...newOriginal, bilder: merged, changed: true}}))
            openDialogView(<MergeReportDlg autoNotes={autoNotes} conflictNotes={conflictNotes}/>)
        })
    }


    const askForUpload = () => {
        openDialogView(<UploadDlg onConfirm={upload}/>);
    }


    const discard = () => {
        openDialog("Alle Änderungen verwerfen?", () => {
            dispatch(storePlan({
                team: glob.team, plan: {
                    id: glob.planOriginal.id,
                    pairs: glob.planOriginal.pairs,
                    changed: false,
                    bilder: glob.planOriginal.bilder
                }
            }))
            dispatch(setSelMode(0))
        });
    }

    return (<div id="bilderplan" className={glob.unit < 7 ? 'mobile' : ''}>
        <header style={{padding: '10px', background: '#f0f0f0', borderBottom: '1px solid #ccc'}}>
            <h1 style={{margin: 0}}>
                {glob.team === '' ? 'Testumgebung' : `Bilderplan ${glob.team.toUpperCase()}-Team`}
            </h1>
            {getHeaderLine(glob.plan, glob.planOriginal)}
            <div>
                <button disabled={!glob.plan.changed || glob.uploading} style={{width: '75px'}} onClick={askForUpload}>{glob.uploading ? '...' : 'Upload'}</button>
                &nbsp;&nbsp;
                <button disabled={!glob.plan.changed} onClick={discard}>Änderungen verwerfen</button>
            </div>
        </header>
        <div id="tab" style={{paddingBottom: '100px', overflowY: 'scroll'}}>
            {
                getTab(glob.tab)
            }
        </div>
        <div id="navigation">
            {
                ['▤', '▦', 'ⓘ'].map((icon, i) => (
                    <div key={i} className={glob.tab === i ? 'selected' : ''} onClick={() => dispatch(setTab(i))}>{icon}</div>
                ))
            }
        </div>
        <DialogManager/>
    </div>)
}

const getHeaderLine = (plan, planOriginal) => {
    const standDate = planOriginal.saved
        ? new Date(planOriginal.saved)
        : null;

    const loadedTime = planOriginal.loaded
        ? new Date(`1970-01-01T${planOriginal.loaded}`)
        : null;

    const heute = new Date();
    const istHeute = standDate &&
        standDate.toDateString() === heute.toDateString();

    const standText = standDate
        ? `Stand: ${standDate.getDate()}.${standDate.getMonth() + 1}.${standDate.getFullYear()} ${standDate.getHours().toString().padStart(2, '0')}:${standDate.getMinutes().toString().padStart(2, '0')}`
        : 'Stand: -';

    const geladenText = planOriginal.loaded
        ? `zul. geprüft: ${
            istHeute
                ? `${loadedTime.getHours().toString().padStart(2, '0')}:${loadedTime.getMinutes().toString().padStart(2, '0')}:${loadedTime.getSeconds().toString().padStart(2, '0')}`
                : planOriginal.loaded.split('T')[0] // falls du loaded später als komplettes Datum speicherst
        }`
        : '';

    return (<div style={{fontSize: '0.9rem', marginTop: '5px'}}>
        <strong>{standText}{plan.changed ? ' (bearbeitet)' : ''}</strong>&nbsp;&nbsp;&nbsp; {geladenText}
    </div>)

}

const getTab = (tab) => {
    switch (tab) {
        case 0:
            return <TabTable/>;
        case 1:
            return <TabBild/>;
        case 2:
            return <TabInfo/>;
        default:
            return null;
    }
};


